import { CommonModule } from '@angular/common'
import { Component, DestroyRef, Input, OnInit, ViewChild, ViewContainerRef } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AbstractControl, FormGroup, ReactiveFormsModule, UntypedFormBuilder, ValidatorFn, Validators } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { ActivatedRoute, Router } from '@angular/router'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { Personas } from '@app/modules/personas/interfaces/persona.interface'
import { LoadingDialogComponent } from '@app/modules/shared/components/loading-dialog/loading-dialog.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { environment } from '@environments/environment'
import { NbFormFieldModule, NbInputModule, NbTooltipModule } from '@nebular/theme'
import { finalize } from 'rxjs/operators'
import { ServerRepresentedEvent } from '../../models/event.model'
import { EventLegalService } from '../../services/event-legal.service'
import { EventService } from '../../services/event.service'
import { DTCMSection } from '../../types/dtcm-setup.type'
import { DtcmSectionComponent } from './dtcm-section/dtcm-section.component'

@Component({
  selector: 'ngx-dtcm-setup',
  templateUrl: './dtcm-setup.component.html',
  styleUrls: ['./dtcm-setup.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, NbFormFieldModule, DtcmSectionComponent, NbTooltipModule, NbInputModule, MatIconModule, LoadingDialogComponent],
  providers: [EventLegalService]
})
export class DtcmSetupComponent implements OnInit {
  @Input() public eventId?: number
  @ViewChild('container', { read: ViewContainerRef }) container: ViewContainerRef
  /**
   * The event to connect the DTCM to
   */
  public event: ServerRepresentedEvent
  /**
   * Personas defined in the event
   */
  public personas: Personas
  /**
   * Flag for any previous DTCM connections on the event
   */
  public isPreviouslyConnected: boolean
  /**
   * DTCM section test connection form
   */
  public form!: FormGroup
  public sections?: DTCMSection[]
  public email: string = ''
  public isLoading = true
  constructor(
    private _alert: AlertService,
    private eventService: EventService,
    private fb: UntypedFormBuilder,
    private router: Router,
    private _eventLegalService: EventLegalService,
    private _authService: AuthService,
    private _destroyRef: DestroyRef
  ) {}
  /**
   * Checks if the event has passed
   */
  public eventCompleted: boolean
  /**
   * The DTCM report url
   */
  public reportUrl: string
  /**
   * Initialize the form, personas and event
   */
  ngOnInit() {
    if (!this.eventId) {
      this.isLoading = false
      this._alert.alert(Alert.ERROR, 'Could not open DTCM setup because the event id is missing.')
      return
    }

    this.eventService
      .getEvent(this.eventId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (event) => {
          this.event = event
          this.eventService
            .getEventForms(this.event.id)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
              next: (form) => {
                this.personas = form.filter((persona) => persona.slug !== 'GEN')
                this._initializeForm()
                const hasDtcmSetup = this.personas.some((persona) => {
                  if (persona?.options?.dtcm) {
                    this.form.get('eventCode').setValue(persona.options.dtcm.eventCode)
                    this.form.get('email').setValue(persona.options.dtcm.email)
                    this.getDtcmDetails()
                    return true
                  }
                })
                if (!hasDtcmSetup) this.isLoading = false
              },
              error: () => {
                this.isLoading = false
                this._alert.alert(Alert.ERROR, 'Error fetching event forms, please contact support if the problem persists.')
              }
            })
          this.reportUrl = `${environment.registrationsApi}dtcm/report/${this._authService.currentWorkspace.slug}/${this.event.slug}`
          this.eventCompleted = Date.now() - Date.parse(this.event.public_end_date) > 0
        },
        error: () => {
          this.isLoading = false
          this._alert.alert(Alert.ERROR, 'Error fetching event, please contact support if the problem persists.')
        }
      })
  }

  /**
   * Initialize the DTCM event form
   */
  private _initializeForm() {
    const connectedPersona = this.personas?.find((o) => !!o.options?.dtcm)
    this.form = this.fb.group({
      eventCode: this.fb.control(connectedPersona?.options?.dtcm?.eventCode ?? '', Validators.required),
      email: this.fb.control(connectedPersona?.options?.dtcm?.email ?? '', [Validators.required, this._customEmailValidator()]),
      permitNo: this.fb.control(connectedPersona?.options?.dtcm?.permitNo ?? '', Validators.required)
    })
  }

  /**
   * Navigate to events list page
   */
  public navigateBack() {
    this.router.navigate(['/events/list'])
  }

  /**
   * Opens the DTCM report
   */
  public openReport(): void {
    window.open(this.reportUrl, '_blank')
  }

  /**
   * Checks if the add section button is disabled
   * @returns True if the add section button is disabled, false otherwise
   */
  public get isAddSectionDisabled(): boolean {
    return document.getElementsByTagName('ngx-dtcm-section')?.length === this.personas?.length || !this.sections
  }

  /**
   * Adds a new section component to the form
   * @param persona The persona to add the section to
   */
  addSection(persona = null): void {
    if (this.isAddSectionDisabled) return
    const newSection = this.container.createComponent(DtcmSectionComponent)
    newSection.instance.event = this.event
    newSection.instance.eventCode = this.form.value.eventCode
    newSection.instance.email = this.form.value.email
    newSection.instance.permitNo = this.form.value.permitNo
    newSection.instance.personas = this.personas
    newSection.instance.sections = this.sections
    if (persona) {
      newSection.instance.persona = persona
      newSection.instance.dtcm = persona?.options?.dtcm || null
    }
    newSection.location.nativeElement.classList.add('bg-white')
    newSection.location.nativeElement.classList.add('border')
    newSection.location.nativeElement.classList.add('mb-3')
    newSection.location.nativeElement.style.borderRadius = '10px'
  }

  noDTCMConnectionAvailable() {
    return !this.personas?.some((persona) => Object.prototype.hasOwnProperty.call(persona.options.dtcm, 'section'))
  }

  public getDtcmDetails() {
    if (!this.form.value.eventCode || this.form.invalid) {
      this.isLoading = false
      return
    }
    const eventCode = this.form.get('eventCode').value
    if (!eventCode) {
      this.isLoading = false
      return
    }
    this.isLoading = true
    this._eventLegalService
      .getSetupDetails('DTCM', this.form.value.eventCode)
      .pipe(
        finalize(() => (this.isLoading = false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((settings) => {
        this.sections = settings.sections
        // Update the validator if the control already exists
        this.form.controls.email.setValidators([Validators.required, this._emailValidator(settings.managers.map((manager) => manager.email))])
        this.form.controls.email.updateValueAndValidity()
      })
  }

  /**
   * Validates the email against the valid emails
   * @param validEmails The valid emails
   * @returns The validator function
   */
  private _emailValidator(validEmails: string[]): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const forbidden = !validEmails.includes(control.value)
      return forbidden ? { invalidEmail: { value: control.value } } : null
    }
  }

  /**
   * Trims the value of the control
   * @param controlName The name of the control to trim its value
   */
  public trimFromControlValue(controlName: string): void {
    this.form.controls[controlName].setValue(this.form.controls[controlName].value.trim())
  }

  /**
   * Custom email validator that checks for proper email format including a dot
   * @returns The validator function
   */
  private _customEmailValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) return null
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
      return emailRegex.test(control.value) ? null : { invalidEmailFormat: { value: control.value } }
    }
  }
}
